import { ColDef, ValueFormatterParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Alert, Button, Checkbox, Select, Tag } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import {
  criticalActivityHeaderComponent,
  criticalActivityIndicatorCellRenderer,
  truncatedTextCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import NoLinkIcon from "components/svg-icons/no-link-icon";
import { GridLoadingIndicator } from "components/widgets";
import {
  ECustomUserTypes,
  EntityType,
  GanttActivityTypes
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  TActivityHookInfo,
  TEditingEntity,
  TEnrichedActivity,
  TEntityLink,
  TFeatureActivityWithInstance,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import { FeatureTypes } from "entity-app/constants";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DateUtils } from "utils/dateutils";
import { matchCustomUserProjectRole } from "utils/utils";
import AIPoweredIcon from "components/svg-icons/ai-powered-icon";
import "./activity-listing.scss";
import {
  getLinkType,
  getTLinkSourceActivityObject
} from "components/feature-entity-linking-page/utils";
import {
  getMaterialIdsForAiFeedback,
  isAiRecommendationTabVisible,
  recordAiActivityLinked,
  recordAiActivityUnlinked,
  markAiRecommendationTabOpened,
  withAiRecommenderFeedback
} from "components/feature-entity-linking-page/utils/ai-link-feedback";
import AIAdvanceLinkingSettings from "pages/bulk-linking/linking-ai-advance-settings";
import { useAISuggestedActivityIdsHook } from "pages/bulk-linking/hooks/ai-suggested-activities";
import { CloseCircleOutlined } from "@ant-design/icons";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import ScheduleFilterBar, {
  FilterState,
  EMPTY_FILTERS,
  buildFilterOptionsFromRows
} from "components/schedule-hierarchy/schedule-filter-bar";
import AIRecommendationCards, {
  filterAiRecommendations,
  getAiRecommendationFilterRow
} from "./ai-recommendation-cards";
const phaseColors: Record<string, string> = {
  Construction: "#1e40af",
  "Pre-Construction": "#6d28d9",
  Commissioning: "#0e7490",
  Closeout: "#374151",
  Root: "#4b5563"
};

const csiColors: Record<string, string> = {
  "03 - Concrete": "#4b5563",
  "05 - Metals": "#57534e",
  "07 - Thermal & Moisture Protection": "#92400e",
  "08 - Openings": "#065f46",
  "09 - Finishes": "#5b21b6",
  "21 - Fire Suppression": "#b91c1c",
  "22 - Plumbing": "#1d4ed8",
  "23 - HVAC": "#047857",
  "26 - Electrical": "#b45309",
  "31 - Earthwork": "#57534e"
};

const activityTypeColors: Record<string, string> = {
  Install: "#047857",
  Demolition: "#b91c1c",
  Inspection: "#b45309",
  Commissioning: "#0e7490",
  Milestone: "#6d28d9",
  Procurement: "#c2410c",
  Submittal: "#4338ca",
  Delivery: "#0369a1",
  Fabrication: "#be185d",
  "Rough-in": "#047857",
  Formwork: "#57534e",
  Pour: "#4b5563",
  Frame: "#92400e",
  Finish: "#5b21b6",
  Testing: "#0e7490",
  Startup: "#1d4ed8"
};

function getCsiColor(csi: string): string {
  if (csiColors[csi]) return csiColors[csi];
  const num = parseInt(csi, 10);
  if (num <= 10) return "#4b5563";
  if (num <= 20) return "#57534e";
  if (num <= 30) return "#0369a1";
  return "#374151";
}

function ActivityTags({ data }: { data: TEnrichedActivity }) {
  if (!data || data.type === 5) return null;

  const tags: Array<{ label: string; color: string }> = [];

  if (data.phase && data.phase !== "Root") {
    tags.push({
      label: data.phase,
      color: phaseColors[data.phase] || "#374151"
    });
  }
  if (data.csi_division) {
    tags.push({
      label: data.csi_division,
      color: getCsiColor(data.csi_division)
    });
  }
  if (data.activity_type) {
    tags.push({
      label: data.activity_type,
      color: activityTypeColors[data.activity_type] || "#374151"
    });
  }
  if (data.location_tags) {
    tags.push({ label: data.location_tags, color: "#1e40af" });
  }

  if (tags.length === 0) return null;

  return (
    <div className="flex items-center gap-1 flex-shrink-0 flex-nowrap ml-auto">
      {tags.map((t, i) => (
        <span
          key={i}
          className="inline-flex items-center px-2 rounded text-white whitespace-nowrap font-semibold"
          style={{
            backgroundColor: t.color,
            fontSize: 10,
            lineHeight: "18px",
            letterSpacing: "0.02em"
          }}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

export function CellWithDateCheckbox(params: any) {
  const { data, isStartDate, onCheckBoxAction, value, isWBSLinkingEnabled } =
    params;
  const checkBoxVisibility = useMemo(() => {
    if (!isWBSLinkingEnabled && data?.type === 5) {
      return value;
    }

    if (data?.type === 5 && isWBSLinkingEnabled) return true;
    return data?.type !== 5;
  }, [data?.type, isWBSLinkingEnabled, value]);

  const date = isStartDate ? data?.start_date : data?.end_date;

  if (!date) return "";
  const dateFormated = DateUtils.format(date, "MM-DD-YYYY");

  // Plain <i> for the date (it is a fixed MM-DD-YYYY that never truncates).
  // TruncatedTextWithTooltip forces w-full, and under antd v6 the checkbox
  // wrapper is inline-flex (was inline-block in v4), so that w-full label
  // stretched the checkbox to fill the cell and defeated the alignment.
  return (
    <div className="flex w-full h-full justify-center items-center">
      {checkBoxVisibility ? (
        <Checkbox
          // antd v6's checkbox wrapper is `align-items: baseline`, which lifts
          // the date label above the box; center it so the date sits mid-row.
          style={{ alignItems: "center" }}
          checked={value}
          onChange={(event) => {
            onCheckBoxAction(event.target.checked, data);
          }}
          data-testid="activitylisting-checkbox-date"
        >
          <i>{dateFormated}</i>
        </Checkbox>
      ) : (
        <i>{dateFormated}</i>
      )}
    </div>
  );
}

const cellWithTaskId = (params: any) => {
  if (params.data.type !== 5 || params?.node?.allChildrenCount) {
    return (
      <TruncatedTextWithTooltip text={params.data.feature_instance.number} />
    );
  }

  if (params.data.type === 5 || !params?.node?.allChildrenCount) {
    return (
      <TruncatedTextWithTooltip
        text={params.data.feature_instance.number}
        disableTooltip
      />
    );
  }

  return (
    <div className="flex items-center">
      <div className="flex h-5 w-5 items-center">
        <NoLinkIcon fill="#3B3B3B33" size={18} />
      </div>
      <i className="mr-2">
        <TruncatedTextWithTooltip
          text={params.data.feature_instance.number}
          tooltipContent="You can only link an activity, milestones can't be linked."
        />
        ;
      </i>
    </div>
  );
};

type OptionType = {
  isLinkedWithStartdate: boolean;
  id: string;
  type: string;
};

/**
 * AI recommendations and schedule filters apply when linking a material
 * (including nested material -> activity from a submittal page). They are
 * hidden when the user is linking activities directly to a submittal.
 */
function resolveRecommenderGates({
  isFeatureFlagEnabled,
  editingEntity,
  selectedEntityInfo,
  hasCompletedRecommenderJob,
  isRecommenderJobPending,
  hasRecommenderJobError,
  materialFeatureId,
  inProgressMaterialIds
}: {
  isFeatureFlagEnabled: (feature: string) => boolean;
  editingEntity: TEditingEntity;
  selectedEntityInfo?: TEntityLink;
  hasCompletedRecommenderJob: boolean;
  isRecommenderJobPending: boolean;
  hasRecommenderJobError: boolean;
  materialFeatureId?: string;
  inProgressMaterialIds?: Set<string>;
}) {
  const advanceFilterFlag =
    isFeatureFlagEnabled("SCHEDULE_ADVANCE_FILTER_V1") ||
    localStorage.getItem("SCHEDULE_ADVANCE_FILTER_V1") === "true";
  const smartLinkingFlag = isFeatureFlagEnabled("SMART_LINKING");

  const isLinkingWithMaterial =
    Boolean(editingEntity.parentEntity) ||
    selectedEntityInfo?.feature_type_id !== FeatureTypes.SUBMITTALS;
  const anyRecommenderFlag = advanceFilterFlag || smartLinkingFlag;
  const isMaterialMatchPending = Boolean(
    materialFeatureId && inProgressMaterialIds?.has(materialFeatureId)
  );

  return {
    isAdvanceFilterEnabled: Boolean(
      isLinkingWithMaterial && advanceFilterFlag && hasCompletedRecommenderJob
    ),
    isSmartSetupEnabled:
      isAiRecommendationTabVisible({
        editingEntity,
        selectedEntityInfo,
        smartLinkingEnabled: smartLinkingFlag,
        hasCompletedRecommenderJob
      }) && !isMaterialMatchPending,
    showRecommenderPending:
      isLinkingWithMaterial &&
      anyRecommenderFlag &&
      (isRecommenderJobPending ||
        (hasCompletedRecommenderJob && isMaterialMatchPending)),
    showRecommenderError:
      isLinkingWithMaterial && anyRecommenderFlag && hasRecommenderJobError,
    isMaterialMatchPending
  };
}

export default function ActivityListing({
  activityListHook,
  editingEntity,
  onUpdateEditingEntityList,
  setEditingEntity,
  selectedEntityInfo
}: {
  activityListHook: TActivityHookInfo;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  selectedEntityInfo?: TEntityLink;
}) {
  const {
    activities,
    isCSVFile,
    hasActive,
    loading: scheduleLoading,
    scheduleVersionsOptions,
    selectedScheduleId,
    createActiveSchedule,
    hasCompletedRecommenderJob = true,
    isRecommenderJobPending = false,
    hasRecommenderJobError = false,
    inProgressMaterialIds,
    recommenderJobId
  } = activityListHook;
  const { tokenContents, isFeatureFlagEnabled, tokenRetrievalState } =
    useContext(ProjectContext);
  const [isWBSLinkingEnabled, setWBSLinkingEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Array<OptionType>>([]);

  const gridRef = useRef<AgGridReact<any>>(null);
  const [activeTab, setActiveTab] = useState<"ai" | "all">("all");
  // Recommendations are keyed by material instance. On the submittal linking
  // page, associating an activity with a linked material uses that material's
  // id (parentEntity); on the material page, entityDetail.id is the material.
  const materialFeatureId =
    editingEntity.parentEntity?.link.featureInstanceId ||
    editingEntity.parentEntity?.link.feature_instance?.id ||
    editingEntity.entityDetail?.id;
  const {
    isAdvanceFilterEnabled,
    isSmartSetupEnabled,
    showRecommenderPending,
    showRecommenderError,
    isMaterialMatchPending
  } = resolveRecommenderGates({
    isFeatureFlagEnabled,
    editingEntity,
    selectedEntityInfo,
    hasCompletedRecommenderJob,
    isRecommenderJobPending,
    hasRecommenderJobError,
    materialFeatureId,
    inProgressMaterialIds
  });
  const shouldTrackAiFeedback = isSmartSetupEnabled;
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [schSearchText, setSchSearchText] = useState("");
  const [showTags, setShowTags] = useState(false);
  const [displayCount, setDisplayCount] = useState<{
    filtered: number;
    total: number;
  } | null>(null);
  const [visibleActivityIds, setVisibleActivityIds] = useState<string[]>([]);

  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const onClose = useCallback(() => {
    setEditingEntity((pre) => {
      if (!pre) return pre;
      return { ...pre, parentEntity: null };
    });
  }, [setEditingEntity]);

  const elementRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = useCallback(
    (event: any) => {
      const domNode = elementRef?.current;
      if (!domNode || !domNode?.contains(event.target)) {
        const skipWindows = document.querySelectorAll(
          ".skip-listing-window, .ant-select-dropdown, .ant-dropdown, .ant-popover, [role='separator']"
        );
        const canSkip = Array.from(skipWindows).some(
          (v) => v?.contains && v.contains(event.target)
        );
        if (!canSkip) {
          setTimeout(() => {
            onClose();
          }, 100);
        }
      }
    },
    [onClose]
  );
  const componentDidMount = useCallback(() => {
    document.addEventListener("click", handleClickOutside, true);
  }, [handleClickOutside]);

  const componentWillUnmount = useCallback(() => {
    document.removeEventListener("click", handleClickOutside, true);
  }, [handleClickOutside]);

  useEffect(() => {
    componentDidMount();
    return componentWillUnmount;
  }, [componentDidMount, componentWillUnmount]);

  const {
    suggestedIds,
    loading: aiSuggestionsLoading,
    refetch: refetchAiSuggestions
  } = useAISuggestedActivityIdsHook({
    featureId: materialFeatureId,
    jobId: recommenderJobId,
    token: tokenRetrievalState.token,
    entiyType: EntityType.Material,
    isEnabled: isSmartSetupEnabled
  });

  const wasMaterialMatchPending = useRef(false);
  useEffect(() => {
    if (wasMaterialMatchPending.current && !isMaterialMatchPending) {
      refetchAiSuggestions?.();
    }
    wasMaterialMatchPending.current = isMaterialMatchPending;
  }, [isMaterialMatchPending, refetchAiSuggestions]);

  useEffect(() => {
    let list: TLinkSource[] = editingEntity?.list || [];

    if (editingEntity?.parentEntity) {
      const parentLink = editingEntity?.list.find(
        (x) =>
          x.feature_instance?.id ===
          editingEntity.parentEntity?.link.featureInstanceId
      );
      if (parentLink) {
        list = parentLink.feature_instance?.links || [];
      }
    }

    const linkedItems = list
      .map((x: TLinkSource) => {
        const sourceTarget = x?.feature_instance;
        const featureActivities = sourceTarget?.feature_activities?.length
          ? sourceTarget?.feature_activities[0]
          : null;

        return sourceTarget?.id
          ? {
              link_type: x.link_type,
              id: sourceTarget.id,
              type: String(featureActivities?.type),
              isLinkedWithStartdate: x.isLinkedWithStartdate
            }
          : undefined;
      })
      .filter((x: OptionType | undefined) => x !== undefined) as OptionType[];

    setSelectedIds(linkedItems);
  }, [editingEntity]);

  const isWBSLinkExist = useMemo(() => {
    return selectedIds?.some((item: any) => item?.type === 5);
  }, [selectedIds]);

  const rowData = useMemo(() => {
    const filteredTasks = activities?.filter(
      (ts: TFeatureActivityWithInstance) => ts.type !== 3 && ts.type !== 4
    );

    const allTasks: (TEnrichedActivity & { org?: string[] })[] = [];

    filteredTasks.forEach((tk) => {
      const task: TEnrichedActivity & { org?: string[] } = { ...tk };
      task.is_suggested =
        suggestedIds && suggestedIds?.length
          ? suggestedIds?.some(
              (item: any) => item?.id === task?.feature_instance_id
            )
          : false;

      allTasks.push(task);
    });

    const addParent = (t: TFeatureActivityWithInstance, org: Array<any>) => {
      if (t.parent) {
        const parent = allTasks?.find(
          (ts: TFeatureActivityWithInstance) =>
            ts.feature_instance_id === t.parent
        );
        if (parent) addParent(parent, org);
      }
      org.push(t.feature_instance_id);
    };
    const tree = allTasks?.map((t: TFeatureActivityWithInstance) => {
      const org: any[] = [];
      if (t) addParent(t, org);
      return { org, ...t };
    });

    return tree;
  }, [activities, suggestedIds]);

  const activitiesById = useMemo(() => {
    const map = new Map<string, TFeatureActivityWithInstance>();
    rowData.forEach((activity) => {
      map.set(activity.feature_instance_id, activity);
    });
    return map;
  }, [rowData]);

  const showAiRecommendations = isSmartSetupEnabled && activeTab === "ai";

  const visibleAiRecommendations = useMemo(
    () =>
      filterAiRecommendations(suggestedIds, {
        searchText: schSearchText,
        filters: isAdvanceFilterEnabled ? filters : EMPTY_FILTERS,
        activitiesById
      }),
    [
      suggestedIds,
      schSearchText,
      isAdvanceFilterEnabled,
      filters,
      activitiesById
    ]
  );

  const aiVisibleCount = visibleAiRecommendations.length;

  const allVisibleIds = useMemo(() => {
    if (displayCount) return visibleActivityIds;
    return rowData
      .filter((row) => row.type !== 5)
      .map((row) => row.feature_instance_id);
  }, [displayCount, visibleActivityIds, rowData]);

  const allVisibleCount = displayCount
    ? displayCount.filtered
    : allVisibleIds.length;

  const resultVisibleCount = showAiRecommendations
    ? aiVisibleCount
    : allVisibleCount;

  // Rows for the filter bar: All Activities vs AI Recommendation tab
  const filterBarRows = useMemo(() => {
    if (!isAdvanceFilterEnabled) return [];

    if (showAiRecommendations) {
      return (suggestedIds || []).map((item) => {
        const activity = (activitiesById.get(
          item.activity_feature_instance_id
        ) || activitiesById.get(item.id)) as TEnrichedActivity | undefined;
        return {
          ...getAiRecommendationFilterRow(item, activity),
          activity_name:
            item.activity_name || activity?.feature_instance?.title,
          activity_id: activity?.feature_instance?.number
        };
      });
    }

    return rowData
      .filter((r) => r.type !== 5)
      .map((r) => {
        const enriched = r as TEnrichedActivity;
        return {
          phase: enriched.phase,
          csi_division: enriched.csi_division,
          activity_type: enriched.activity_type,
          location_tags: enriched.location_tags,
          activity_name: enriched.feature_instance?.title,
          activity_id: enriched.feature_instance?.number
        };
      });
  }, [
    rowData,
    isAdvanceFilterEnabled,
    showAiRecommendations,
    suggestedIds,
    activitiesById
  ]);

  const enrichedFilterOptions = useMemo(
    () => buildFilterOptionsFromRows(filterBarRows),
    [filterBarRows]
  );

  // Sync filter bar selections → AG Grid hidden column filters
  useEffect(() => {
    if (!isAdvanceFilterEnabled) return;
    const api = gridRef.current?.api;
    if (!api) return;

    const applyFilters = async () => {
      const cols: Array<{ colId: string; values: string[] }> = [
        { colId: "phase", values: filters.phase },
        { colId: "activity_type", values: filters.activityType },
        { colId: "csi_division", values: filters.csiDivision },
        { colId: "location_tags", values: filters.location }
      ];

      for (const { colId, values } of cols) {
        const filterInstance = await api.getColumnFilterInstance(colId);
        if (filterInstance) {
          filterInstance.setModel(values.length > 0 ? { values } : null);
        }
      }

      api.onFilterChanged();

      // Auto-expand when any filter is active, collapse when all cleared
      const hasActiveFilters =
        filters.phase.length > 0 ||
        filters.csiDivision.length > 0 ||
        filters.activityType.length > 0 ||
        filters.location.length > 0 ||
        schSearchText.trim().length > 0;

      if (hasActiveFilters) {
        api.expandAll();
      } else {
        api.collapseAll();
      }
    };

    applyFilters();
  }, [isAdvanceFilterEnabled, filters, schSearchText]);

  // Track displayed row count after AG Grid filters change
  const handleFilterChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !isAdvanceFilterEnabled) return;

    let filtered = 0;
    const ids: string[] = [];

    api.forEachNodeAfterFilter((node) => {
      if (!node.data) return;
      if (node.data.type !== 5) {
        filtered++;
        ids.push(node.data.feature_instance_id);
      }
    });

    const total = rowData.filter((r) => r.type !== 5).length;
    setVisibleActivityIds(ids);
    setDisplayCount((prev) => {
      if (prev && prev.filtered === filtered && prev.total === total)
        return prev;
      return { filtered, total };
    });
  }, [isAdvanceFilterEnabled, rowData]);

  const applyAiLinkFeedback = useCallback(
    (
      entity: TEditingEntity,
      {
        activityId,
        checked,
        wasAlreadyLinked
      }: {
        activityId: string;
        checked: boolean;
        wasAlreadyLinked: boolean;
      }
    ): TEditingEntity => {
      if (!shouldTrackAiFeedback) return entity;
      const materialIds = getMaterialIdsForAiFeedback(
        entity,
        selectedEntityInfo
      );
      if (checked) {
        if (wasAlreadyLinked) return entity;
        return (
          withAiRecommenderFeedback(entity, (feedback) =>
            recordAiActivityLinked(feedback, { activityId, materialIds })
          ) ?? entity
        );
      }
      return (
        withAiRecommenderFeedback(entity, (feedback) =>
          recordAiActivityUnlinked(feedback, { activityId, materialIds })
        ) ?? entity
      );
    },
    [shouldTrackAiFeedback, selectedEntityInfo]
  );

  const handleAiTabClick = useCallback(() => {
    setActiveTab("ai");
    if (!shouldTrackAiFeedback) return;
    setEditingEntity((prev) =>
      withAiRecommenderFeedback(prev, markAiRecommendationTabOpened)
    );
  }, [shouldTrackAiFeedback, setEditingEntity]);

  const updateForChildEntity = useCallback(
    (
      isCheckBoxFromStartDate: boolean,
      checked: boolean,
      item: TFeatureActivityWithInstance
    ) => {
      if (!editingEntity?.parentEntity || !editingEntity?.parentEntity?.link)
        return;
      const materialLink = editingEntity.list.find(
        (x) =>
          x.featureInstanceId ===
          editingEntity?.parentEntity?.link.featureInstanceId
      );

      const material = materialLink?.feature_instance;
      if (!material) return;
      const remainingList = editingEntity.list.filter(
        (x) => x.featureInstanceId !== material.id
      );

      if (!material || !material.links) return;

      const wasAlreadyLinked = material.links.some(
        (x) => x.feature_instance?.id === item.feature_instance_id
      );

      let activityList: TLinkSource[] = [...material.links];

      if (checked) {
        const existingIndex = material.links.findIndex(
          (i) =>
            i.feature_instance?.id === item.feature_instance_id &&
            i.isLinkedWithStartdate === !isCheckBoxFromStartDate
        );
        if (existingIndex !== -1) {
          const activityItem = material.links[existingIndex];

          const updatedlist = [
            ...material.links.slice(0, existingIndex),
            {
              ...activityItem,
              isLinkedWithStartdate: isCheckBoxFromStartDate,
              link_type: getLinkType(
                activityItem.reverse_dependency,
                isCheckBoxFromStartDate
              )
            },
            ...material.links.slice(existingIndex + 1)
          ];
          activityList = updatedlist;
        } else {
          const newActivity = getTLinkSourceActivityObject(
            item,
            isCheckBoxFromStartDate
          );
          activityList.push(newActivity);
        }
      } else {
        activityList = activityList.filter(
          (x) => !(x.feature_instance?.id === item.feature_instance_id)
        );
      }

      const newEditingEntity: TEditingEntity = editingEntity
        ? {
            ...editingEntity,
            list: [
              ...remainingList,
              {
                ...materialLink,
                feature_instance: { ...material, links: activityList }
              }
            ]
          }
        : editingEntity;
      setEditingEntity(
        applyAiLinkFeedback(newEditingEntity, {
          activityId: item.feature_instance_id,
          checked,
          wasAlreadyLinked
        })
      );
    },
    [editingEntity, setEditingEntity, applyAiLinkFeedback]
  );

  const onChangeCheckbox = useCallback(
    (
      isCheckBoxFromStartDate: boolean,
      checked: boolean,
      item: TFeatureActivityWithInstance
    ) => {
      if (editingEntity.parentEntity) {
        updateForChildEntity(isCheckBoxFromStartDate, checked, item);
        return;
      }

      const wasAlreadyLinked = editingEntity.list.some(
        (x) => x.feature_instance?.id === item.feature_instance_id
      );

      if (checked) {
        const existingIndex = editingEntity.list.findIndex(
          (i) =>
            i.feature_instance?.id === item.feature_instance_id &&
            i.isLinkedWithStartdate === !isCheckBoxFromStartDate
        );
        if (existingIndex !== -1) {
          const activityItem = editingEntity.list[existingIndex];

          const updatedlist = [
            ...editingEntity.list.slice(0, existingIndex),
            {
              ...activityItem,
              isLinkedWithStartdate: isCheckBoxFromStartDate,
              link_type: getLinkType(
                activityItem.reverse_dependency,
                isCheckBoxFromStartDate
              )
            },
            ...editingEntity.list.slice(existingIndex + 1)
          ];

          onUpdateEditingEntityList({
            updatedlist
          });
          return;
        }

        const newActivity = getTLinkSourceActivityObject(
          item,
          isCheckBoxFromStartDate
        );
        onUpdateEditingEntityList({
          updatedlist: [...editingEntity.list, newActivity]
        });
      } else {
        onUpdateEditingEntityList({
          updatedlist: editingEntity.list.filter(
            (x) => !(x.feature_instance?.id === item.feature_instance_id)
          )
        });
      }

      if (shouldTrackAiFeedback) {
        setEditingEntity((prev) =>
          prev
            ? applyAiLinkFeedback(prev, {
                activityId: item.feature_instance_id,
                checked,
                wasAlreadyLinked
              })
            : prev
        );
      }
    },
    [
      editingEntity.parentEntity,
      editingEntity.list,
      onUpdateEditingEntityList,
      updateForChildEntity,
      applyAiLinkFeedback,
      setEditingEntity,
      shouldTrackAiFeedback
    ]
  );

  const aiIconCellRenderer = ({ data }: any) => {
    return data?.is_suggested ? (
      <div className="items-center w-1">
        <AIPoweredIcon />
      </div>
    ) : null;
  };

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    const colArr: ColDef[] = [
      {
        colId: "ai_icon",
        field: "ai_icon",
        headerName: "",
        headerTooltip: "",
        cellRenderer: aiIconCellRenderer,
        minWidth: 20,
        maxWidth: 20,
        resizable: false,
        showRowGroup: false,
        filter: true,
        valueGetter: ({ data }: any) => {
          return data?.is_suggested ? "showAiIcon" : "hideAiIcon";
        }
      },
      ...(isAdvanceFilterEnabled
        ? [
            {
              colId: "text",
              field: "feature_instance.title",
              headerName: "Name",
              headerTooltip: "NAME",
              filter: true,
              flex: 1,
              minWidth: 250,
              showRowGroup: true,
              cellRenderer: "agGroupCellRenderer",
              cellRendererParams: {
                innerRenderer: showTags
                  ? (params: any) => {
                      const { data } = params;
                      if (!data) return null;
                      return (
                        <div className="flex items-center w-full gap-3">
                          <span className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                            <TruncatedTextWithTooltip
                              text={data.feature_instance?.title}
                            />
                          </span>
                          <ActivityTags data={data} />
                        </div>
                      );
                    }
                  : truncatedTextCellRenderer
              }
            } as ColDef,
            {
              colId: "source_task_id",
              field: "feature_instance.number",
              headerName: "ID",
              headerTooltip: "ID",
              filter: true,
              cellRenderer: cellWithTaskId,
              sort: "desc" as const,
              minWidth: 150
            } as ColDef
          ]
        : [
            {
              colId: "text",
              field: "feature_instance.title",
              headerName: "Name",
              headerTooltip: "NAME",
              showRowGroup: true,
              filter: true,
              flex: 1,
              minWidth: 250,
              cellRenderer: "agGroupCellRenderer",
              cellRendererParams: {
                innerRenderer: truncatedTextCellRenderer
              }
            } as ColDef,
            {
              colId: "source_task_id",
              field: "feature_instance.number",
              headerName: "ID",
              headerTooltip: "ID",
              filter: true,
              cellRenderer: cellWithTaskId,
              sort: "desc",
              minWidth: 150
            } as ColDef
          ]),
      {
        colId: "critical",
        field: "critical",
        headerName: "",
        headerTooltip: "CRITICAL ACTIVITY",
        headerComponentParams: criticalActivityHeaderComponent,
        cellRenderer: criticalActivityIndicatorCellRenderer,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params?.value === true ? "Critical" : "Non Critical";
          },
          comparator: (valA: any, valB: any) => {
            if (valA === valB) return 0;
            return valA > valB ? -1 : 1;
          }
        },
        minWidth: 90,
        maxWidth: 90,
        getQuickFilterText: () => ""
      },
      {
        colId: "start_date",
        field: "start_date",
        headerName: "Start Date",
        headerTooltip: "START DATE",
        headerClass: "dateHedderCell",
        // Make the cell a flex box so the checkbox+date renderer is vertically
        // centered (ag-grid centers plain text via line-height, but not a
        // flex/block renderer — it would otherwise sit at the top of the cell).
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: true,
          onCheckBoxAction: (
            checked: boolean,
            data: TFeatureActivityWithInstance
          ) => onChangeCheckbox(true, checked, data),
          isWBSLinkingEnabled
        },
        valueGetter: ({ data }) =>
          selectedIds.some(
            (x) => x.id === data.feature_instance_id && x.isLinkedWithStartdate
          ),
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => "",
        pinned: "right"
      },
      {
        colId: "end_date",
        field: "end_date",
        headerName: "End Date",
        headerTooltip: "END DATE",
        headerClass: "dateHedderCell",
        // See start_date: flex cell so the renderer is vertically centered.
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: CellWithDateCheckbox,
        cellRendererParams: {
          isStartDate: false,
          onCheckBoxAction: (
            checked: boolean,
            data: TFeatureActivityWithInstance
          ) => onChangeCheckbox(false, checked, data),
          isWBSLinkingEnabled
        },
        valueGetter: ({ data }) =>
          selectedIds.some(
            (x) => x.id === data.feature_instance_id && !x.isLinkedWithStartdate
          ),
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => "",
        pinned: "right"
      },
      {
        colId: "total_slack",
        field: "total_slack",
        headerName: "Activity Float",
        headerTooltip: "ACTIVITY FLOAT",
        cellStyle: (params: any) => {
          if (params?.data?.critical) {
            return { fontWeight: "bold" };
          }
          return { fontWeight: "normal" };
        },
        valueGetter: (params) => {
          if (
            params?.data?.total_slack === null ||
            params.data.type === GanttActivityTypes.Wps
          )
            return "";
          const float = Math.floor((params?.data?.total_slack || 0) / 8);
          return `${float} days`;
        },
        minWidth: 100,
        getQuickFilterText: () => "",
        cellRenderer: truncatedTextCellRenderer
      }
    ] as ColDef[];

    // Add hidden filter columns when advance filter flag is ON
    if (isAdvanceFilterEnabled) {
      colArr.push(
        {
          colId: "phase",
          field: "phase",
          headerName: "Phase",
          hide: true,
          filter: "agSetColumnFilter",
          suppressFiltersToolPanel: false
        },
        {
          colId: "activity_type",
          field: "activity_type",
          headerName: "Activity Type",
          hide: true,
          filter: "agSetColumnFilter",
          suppressFiltersToolPanel: false
        },
        {
          colId: "csi_division",
          field: "csi_division",
          headerName: "CSI Division",
          hide: true,
          filter: "agSetColumnFilter",
          suppressFiltersToolPanel: false
        },
        {
          colId: "location_tags",
          field: "location_tags",
          headerName: "Location",
          hide: true,
          filter: "agSetColumnFilter",
          suppressFiltersToolPanel: false
        }
      );
    }

    if (!isGCUser || isCSVFile) {
      return colArr
        ?.filter(
          (col: ColDef) =>
            col.colId !== "critical" && col.colId !== "total_slack"
        )
        .map((x) => {
          if (x.colId === "source_task_id") {
            return {
              ...x,
              minWidth: 200,
              width: 250
            };
          }
          if (x.colId === "text") {
            return {
              ...x,
              minWidth: 300,
              width: 400
            };
          }
          return x;
        });
    }
    return colArr;
  }, [
    isWBSLinkingEnabled,
    isGCUser,
    isCSVFile,
    onChangeCheckbox,
    selectedIds,
    isAdvanceFilterEnabled,
    showTags
  ]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: false,
      menuTabs: [],
      resizable: true,
      editable: false,
      cellRendererParams: {
        suppressCount: true
      },
      comparator: comparatorWithEmptyAtBottom,
      suppressFiltersToolPanel: true
    };
  }, []);

  const getDataPath = useMemo(() => {
    return (data: any) => {
      return data.org;
    };
  }, []);

  const rowClass = "ai-row-suggested";

  const getRowClass = useCallback(({ data }: { data?: any }) => {
    if (data?.is_suggested) {
      return rowClass;
    }
    return "ai-row-unsuggested";
  }, []);

  const handleSearch = useCallback((value: string) => {
    const text = (value || "").toLowerCase().trim();
    gridRef.current?.api?.setGridOption("quickFilterText", text);
    setSchSearchText(text);
  }, []);

  const handleExpandAll = useCallback(() => {
    gridRef.current?.api?.expandAll();
  }, []);

  const handleCollapseAll = useCallback(() => {
    gridRef.current?.api?.collapseAll();
  }, []);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden activity-listing ai-row-style skip-listing-window">
      <div className="flex flex-col w-full px-6 py-2">
        <div className="flex items-center justify-between pb-1">
          <div className="flex font-bold space-x-1 pb-1 items-center">
            <div>List of Activities</div>
            {isAdvanceFilterEnabled && (
              <span className="text-xs font-normal text-gray-500 ml-1">
                {displayCount
                  ? displayCount.filtered < displayCount.total
                    ? `(${displayCount.filtered.toLocaleString()} of ${displayCount.total.toLocaleString()})`
                    : `(${displayCount.total.toLocaleString()})`
                  : `(${rowData.filter((r) => r.type !== 5).length.toLocaleString()})`}
              </span>
            )}

            {editingEntity.parentEntity && (
              <div className="flex pl-1 text-black text-opacity-60">
                (
                <div
                  className="italic px-1"
                  data-testid="activitylisting-parent-entity-title"
                >
                  {editingEntity.parentEntity.link.feature_instance?.title}
                </div>
                )
              </div>
            )}
          </div>
          {isAdvanceFilterEnabled ? (
            <div className="flex items-center gap-2">
              {editingEntity.parentEntity && (
                <div
                  className="flex justify-center items-center"
                  data-testid="activitylisting-close-button"
                >
                  <CloseCircleOutlined title="Close" onClick={onClose} />
                </div>
              )}
              <AIAdvanceLinkingSettings
                isWBSLinkExist={isWBSLinkExist}
                onWBSEnableAction={(isChecked: boolean) =>
                  setWBSLinkingEnabled(isChecked)
                }
              />
            </div>
          ) : (
            editingEntity.parentEntity && (
              <div
                className="flex justify-center items-center mr-1"
                data-testid="activitylisting-close-button"
              >
                <CloseCircleOutlined title="Close" onClick={onClose} />
              </div>
            )
          )}
        </div>

        {!hasActive ? (
          <div className="flex items-center space-x-3 pt-2">
            <Select
              options={scheduleVersionsOptions}
              className="w-64"
              value={selectedScheduleId}
              onChange={(id) => {
                if (id && typeof id === "string") createActiveSchedule(id);
              }}
              data-testid="activitylisting-select-scheduleid"
            />
            <CiqAgSearch
              placeholder="Search Activity Name or ID"
              className="w-full"
              onSearch={handleSearch}
              data-testid="activitylisting-search-activity-name-id"
            />
            <Button
              className="flex-shrink-0 whitespace-nowrap"
              onClick={handleExpandAll}
              data-testid="activitylisting-expand-all"
            >
              Expand All
            </Button>
            <Button
              className="flex-shrink-0 whitespace-nowrap"
              onClick={handleCollapseAll}
              data-testid="activitylisting-collapse-all"
            >
              Collapse All
            </Button>
            {!isAdvanceFilterEnabled && (
              <AIAdvanceLinkingSettings
                isWBSLinkExist={isWBSLinkExist}
                onWBSEnableAction={(isChecked: boolean) =>
                  setWBSLinkingEnabled(isChecked)
                }
              />
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-3 pt-2">
            <CiqAgSearch
              placeholder="Search Activity Name or ID"
              className="w-full"
              data-testid="activitylisting-search-activity-name-id"
              onSearch={handleSearch}
            />
            <Button
              className="flex-shrink-0 whitespace-nowrap"
              onClick={handleExpandAll}
              data-testid="activitylisting-expand-all"
            >
              Expand All
            </Button>
            <Button
              className="flex-shrink-0 whitespace-nowrap"
              onClick={handleCollapseAll}
              data-testid="activitylisting-collapse-all"
            >
              Collapse All
            </Button>
            {!isAdvanceFilterEnabled && (
              <AIAdvanceLinkingSettings
                isWBSLinkExist={isWBSLinkExist}
                onWBSEnableAction={(isChecked: boolean) =>
                  setWBSLinkingEnabled(isChecked)
                }
              />
            )}
          </div>
        )}
      </div>

      {showRecommenderPending && (
        <div className="px-6 py-2" data-testid="link-recommender-pending">
          <Alert
            type="info"
            showIcon
            title="AI analysis is still running in the background. Filters and AI recommendations will appear when it's ready."
          />
        </div>
      )}

      {showRecommenderError && (
        <div className="px-6 py-2" data-testid="link-recommender-error">
          <Alert
            type="error"
            showIcon
            title="AI analysis failed. Filters and AI recommendations are unavailable."
          />
        </div>
      )}

      {isAdvanceFilterEnabled && (
        <ScheduleFilterBar
          filters={filters}
          onFilterChange={setFilters}
          showTags={showTags}
          onShowTagsChange={setShowTags}
          totalCount={
            displayCount?.total ?? rowData.filter((r) => r.type !== 5).length
          }
          filteredCount={
            isSmartSetupEnabled
              ? resultVisibleCount
              : (displayCount?.filtered ??
                rowData.filter((r) => r.type !== 5).length)
          }
          filterOptions={enrichedFilterOptions}
          allRows={filterBarRows}
          searchText={schSearchText}
          hideCount
        />
      )}

      {isSmartSetupEnabled && (
        <div className="activity-listing-subtabs px-6" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "all"}
            className={`activity-listing-subtab${
              activeTab === "all" ? " is-active" : ""
            }`}
            onClick={() => setActiveTab("all")}
            data-testid="activity-listing-tab-all"
          >
            All Activities
            <span
              className="activity-listing-subtab-count"
              data-testid="activity-listing-all-count"
            >
              {allVisibleCount}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "ai"}
            className={`activity-listing-subtab${
              activeTab === "ai" ? " is-active" : ""
            }`}
            onClick={handleAiTabClick}
            data-testid="activity-listing-tab-ai"
          >
            AI Recommendation
            <Tag data-testid="activity-listing-ai-beta">Beta</Tag>
            <span
              className="activity-listing-subtab-count"
              data-testid="activity-listing-ai-count"
            >
              {aiVisibleCount}
            </span>
          </button>
        </div>
      )}

      {showAiRecommendations && (
        <AIRecommendationCards
          recommendations={suggestedIds}
          loading={aiSuggestionsLoading}
          searchText={schSearchText}
          filters={isAdvanceFilterEnabled ? filters : EMPTY_FILTERS}
          showTags={showTags}
          activitiesById={activitiesById}
          selectedIds={selectedIds}
          isWBSLinkingEnabled={isWBSLinkingEnabled}
          onChangeCheckbox={onChangeCheckbox}
        />
      )}

      <div
        className={
          showAiRecommendations ? "hidden" : "flex-1 min-h-0 flex flex-col"
        }
      >
        <AgGridReact
          className="ag-theme-alpine non-border ai-row-style"
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={false}
          headerHeight={40}
          treeData
          loading={scheduleLoading}
          loadingOverlayComponent={GridLoadingIndicator}
          rowSelection={{
            mode: "singleRow",
            checkboxes: false
          }}
          groupDefaultExpanded={-1}
          getDataPath={getDataPath}
          groupDisplayType="custom"
          suppressCellFocus
          suppressDragLeaveHidesColumns
          suppressMovableColumns
          excludeChildrenWhenTreeDataFiltering
          getRowId={(param) => param.data?.feature_instance_id}
          rowClass={rowClass}
          getRowClass={getRowClass}
          onFilterChanged={handleFilterChanged}
        />
      </div>
    </div>
  );
}
